import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // User roles
  const ROLES = {
    MANAGER: 1,
    PASSENGER: 2,
    CONDUCTOR: 3
  };

  // Login function - handles password login for Manager and Conductor
  const login = async (credentials) => {
    try {
      setLoading(true);
      
      // Import authApi dynamically to avoid circular dependencies
      const { authApi } = await import('../services/api');
      
      // Call backend login API
      const response = await authApi.login(credentials.username, credentials.password);
      
      // Map role name to role ID
      let roleId = ROLES.PASSENGER;
      if (response.role === 'Manager') {
        roleId = ROLES.MANAGER;
      } else if (response.role === 'Conductor' || response.roleID === 3 || response.roleId === 3) {
        roleId = ROLES.CONDUCTOR; // RoleID 3 = Passenger/Driver/Conductor
      } else if (response.role === 'Passenger') {
        roleId = ROLES.PASSENGER;
      }
      
      const userData = {
        id: response.userID || 0,
        username: response.username,
        fullName: response.fullName,
        role: roleId,
        roleName: response.role,
        permissions: response.permissions || []
      };
      
      // For conductor, username is the bus number (last 4 digits)
      if (roleId === ROLES.CONDUCTOR) {
        userData.busNumber = credentials.username; // Store bus number from username
      }
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', response.token);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      
      // Extract error message from API response
      let errorMessage = 'Invalid username or password. Please check your credentials.';
      
      if (error.details) {
        // Error from API service with details
        errorMessage = error.details.message || error.details.error || error.message || errorMessage;
      } else if (error.message) {
        // Check if message contains JSON
        try {
          const jsonMatch = error.message.match(/\{.*\}/);
          if (jsonMatch) {
            const errorData = JSON.parse(jsonMatch[0]);
            errorMessage = errorData.message || errorData.error || errorMessage;
          } else {
            errorMessage = error.message;
          }
        } catch (e) {
          errorMessage = error.message;
        }
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  const isRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  // Listen for logout events from API interceptor (401 errors)
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  // Helper function to decode JWT token and check expiration
  const isTokenExpired = (token) => {
    try {
      if (!token) return true;
      
      // JWT token has 3 parts: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid token format');
        return true;
      }
      
      // Decode the payload (second part)
      const payload = parts[1];
      // Add padding if needed for base64 decoding
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decodedPayload = JSON.parse(atob(paddedPayload));
      
      // Check expiration (exp is in seconds, Date.now() is in milliseconds)
      const expirationTime = decodedPayload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      // Token is expired if current time is past expiration time
      const expired = currentTime >= expirationTime;
      
      if (expired) {
        console.warn('⏰ Token is expired. Expiration:', new Date(expirationTime), 'Current:', new Date(currentTime));
      } else {
        console.log('✅ Token is valid. Expires at:', new Date(expirationTime));
      }
      
      return expired;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true; // If we can't decode, consider it expired
    }
  };

  // Check if user is logged in on app start and validate token
  useEffect(() => {
    const validateTokenAndRestoreUser = async () => {
      const savedUser = localStorage.getItem('user');
      const savedToken = localStorage.getItem('token');
      
      if (!savedUser || !savedToken) {
        setLoading(false);
        return;
      }

      // FIRST: Check if token is expired by decoding JWT
      if (isTokenExpired(savedToken)) {
        console.warn('❌ Token is expired - clearing session without API call');
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setLoading(false);
        
        // Dispatch event to show OTP modal
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }, 500); // Small delay to ensure UI is ready
        return;
      }

      // Token is not expired - proceed with validation
      try {
        const parsedUser = JSON.parse(savedUser);
        
        // Ensure role is mapped correctly if it's stored as role name
        if (parsedUser.roleName && !parsedUser.role) {
          if (parsedUser.roleName === 'Manager' || parsedUser.roleName === 'manager') {
            parsedUser.role = ROLES.MANAGER;
          } else if (parsedUser.roleName === 'Conductor' || parsedUser.roleName === 'conductor') {
            parsedUser.role = ROLES.CONDUCTOR;
          } else {
            parsedUser.role = ROLES.PASSENGER;
          }
        }

        // Token is not expired, restore user immediately
        console.log('✅ Token is valid (not expired), restoring user session');
        setUser(parsedUser);
        setLoading(false);
        
        // Optionally validate with API call in background (non-blocking)
        // This is just to double-check, but we already know token is valid from expiration check
        try {
          const { bookingApi } = await import('../services/api');
          await bookingApi.getTodayDashboardStats();
          console.log('✅ Token validated with API - confirmed valid');
        } catch (error) {
          // If API validation fails, token might be invalid for other reasons
          // But we already restored user, so just log the warning
          console.warn('⚠️ API validation failed, but token expiration check passed:', error.message);
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setLoading(false);
      }
    };

    validateTokenAndRestoreUser();
  }, []);

  const value = {
    user,
    setUser,
    login,
    logout,
    loading,
    hasPermission,
    isRole,
    ROLES
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 